import { datadogLogs } from "@datadog/browser-logs";
import { datadogRum } from "@datadog/browser-rum";

import { viteEnv } from "utils/vite-env.util";

import {
  downgradeBoxPreviewDetachedScrollLog,
  tagBoxPreviewDetachedScrollRumError
} from "./box-preview-console-noise";
import {
  downgradeBrowserExtensionLog,
  tagBrowserExtensionRumError
} from "./browser-extension-errors";
import { flushPendingFlagEvaluations } from "./datadog-flag-evaluations";
import { createResizeObserverLoopSampler } from "./resize-observer-loop-sampler";

export default function setupDatadog() {
  const sampleResizeObserverLoop = createResizeObserverLoopSampler();
  if (viteEnv.VITE_ENV !== "local") {
    datadogRum.init({
      applicationId: viteEnv.VITE_DATADOG_APPLICATION_ID!,
      clientToken: viteEnv.VITE_DATADOG_CLIENT_TOKEN!,
      site: viteEnv.VITE_DATADOG_SITE!,
      service: `suno-app-${viteEnv.VITE_ENV}`,
      version: __APP_VERSION__,
      env: viteEnv.VITE_ENV,
      //  env: 'production',
      //  version: '1.0.0',
      allowedTracingUrls: [viteEnv.VITE_SUNO_API!, viteEnv.VITE_SUNO_WS!],
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100, // if not included, the default is 100
      profilingSampleRate: 100,
      trackResources: true,
      trackLongTasks: true,
      trackUserInteractions: true,
      defaultPrivacyLevel: "allow",
      enableExperimentalFeatures: ["feature_flags"],
      beforeSend: (event, context) => {
        // Drop before enriching, so a discarded event costs no user lookup.
        if (
          event.type === "error" &&
          event.error?.message?.includes("ResizeObserver loop")
        ) {
          return false;
        }
        // ENG-2354: Box Preview's detached-scroll console error, kept but tagged.
        tagBoxPreviewDetachedScrollRumError(event);
        // ENG-2600: errors thrown entirely by a browser extension, kept but tagged.
        tagBrowserExtensionRumError(event);

        // Add practice context as tags for APM grouping
        const user = datadogRum.getUser();
        if (user && user.practice) {
          // Add as both context and direct properties for better APM support
          event.context = {
            ...event.context,
            practice_id: user.practice,
            practice_name: user.practice_name
          };

          // Also add as direct properties on the event
          event.practice_id = user.practice;
          event.practice_name = user.practice_name;
        }
        return true;
      }
    });
    flushPendingFlagEvaluations();
    datadogLogs.init({
      clientToken: viteEnv.VITE_DATADOG_CLIENT_TOKEN!,
      site: viteEnv.VITE_DATADOG_SITE!,
      forwardErrorsToLogs: true,
      sessionSampleRate: 100,
      service: `suno-app-${viteEnv.VITE_ENV}`,
      env: viteEnv.VITE_ENV,
      version: __APP_VERSION__,
      beforeSend: (log, context) => {
        // ENG-1898: one aggregated warn per minute instead of the raw
        // per-frame stream. RUM drops these outright (above); Logs keeps the
        // onset and the rate.
        if (!sampleResizeObserverLoop(log)) return false;
        // ENG-2354: Box Preview's detached-scroll console error, kept as a warn.
        downgradeBoxPreviewDetachedScrollLog(log, context?.handlingStack);
        // ENG-2600: errors thrown entirely by a browser extension, kept as a warn.
        downgradeBrowserExtensionLog(log);
        if (
          log.http &&
          (log.http.url.startsWith("https://events.launchdarkly.com") ||
            log.http.url.startsWith("https://api.box.com"))
        ) {
          return false;
        } else if (
          log.error &&
          log.error?.kind === "SyntaxError" &&
          log.error?.message?.includes("Unexpected token '<'")
        ) {
          return false;
        }

        // Add practice context to all log events
        const user = datadogRum.getUser();
        if (user && user.practice) {
          log.practice_id = user.practice;
          log.practice_name = user.practice_name;
        }

        return true;
      }
    });
    datadogRum.startSessionReplayRecording();
  }
}
